import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FilePdf, ArrowsClockwise } from "@phosphor-icons/react";
import api from "../lib/api";
import { Button } from "./ui/button";

/**
 * Partner PDF download control.
 *
 * Fetches lightweight metadata so the operator sees how many *Approved*
 * ideas are in this stream BEFORE downloading. The PDF itself is
 * regenerated on every click — no stale cache, always reflects current
 * approval state.
 *
 * variant="prominent"  — full-size button used on StreamDetail header
 * variant="compact"    — small inline link used on stream listing cards
 */
export default function PartnerPdfButton({ streamId, streamName, variant = "prominent" }) {
    const [meta, setMeta] = useState(null);
    const [loadingMeta, setLoadingMeta] = useState(true);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const fetchMeta = async () => {
            try {
                const r = await api.get(`/streams/${streamId}/partner-pdf-meta`);
                if (!cancelled) setMeta(r.data);
            } catch {
                if (!cancelled) setMeta({ total: 0, buckets: [] });
            } finally {
                if (!cancelled) setLoadingMeta(false);
            }
        };
        fetchMeta();
        return () => { cancelled = true; };
    }, [streamId]);

    const totalApproved = meta?.total ?? 0;
    const disabled = downloading || loadingMeta || totalApproved === 0;

    const download = async (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (disabled) return;
        setDownloading(true);
        try {
            const r = await api.get(`/streams/${streamId}/partner-pdf`, {
                responseType: "blob",
            });
            // Filename from Content-Disposition; fallback to derived name
            let filename = `haevn-${(streamName || "stream").toLowerCase().replace(/[^a-z0-9-]+/g, "-")}-partner-brief.pdf`;
            const cd = r.headers?.["content-disposition"] || r.headers?.["Content-Disposition"];
            if (cd) {
                const m = /filename="?([^";]+)"?/i.exec(cd);
                if (m && m[1]) filename = m[1];
            }
            const blob = new Blob([r.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Partner PDF downloaded · ${totalApproved} approved ${totalApproved === 1 ? "idea" : "ideas"}`);
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || "Could not generate PDF";
            toast.error(typeof detail === "string" ? detail : "Could not generate PDF");
        } finally {
            setDownloading(false);
        }
    };

    const summary = loadingMeta
        ? "Counting approved ideas…"
        : totalApproved === 0
        ? "No approved ideas yet"
        : `${totalApproved} approved ${totalApproved === 1 ? "idea" : "ideas"} ready`;

    if (variant === "compact") {
        return (
            <button
                type="button"
                onClick={download}
                disabled={disabled}
                data-testid={`partner-pdf-download-${streamId}`}
                title={summary}
                className={[
                    "text-xs font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1.5 border-b pb-0.5",
                    disabled
                        ? "border-border text-muted-foreground/60 cursor-not-allowed"
                        : "border-border hover:text-primary hover:border-primary",
                ].join(" ")}
            >
                {downloading
                    ? <ArrowsClockwise size={11} className="animate-spin" />
                    : <FilePdf size={11} weight="duotone" />}
                Partner PDF
                {totalApproved > 0 && (
                    <span className="ml-0.5 text-[10px] tabular-nums">({totalApproved})</span>
                )}
            </button>
        );
    }

    // prominent
    return (
        <div className="inline-flex flex-col items-stretch">
            <Button
                data-testid={`partner-pdf-download-${streamId}`}
                size="sm"
                variant="outline"
                className="rounded-sm h-8"
                onClick={download}
                disabled={disabled}
                title={summary}
            >
                {downloading
                    ? <ArrowsClockwise size={13} className="mr-1.5 animate-spin" />
                    : <FilePdf size={13} weight="duotone" className="mr-1.5" />}
                {downloading ? "Generating…" : "Download Partner PDF"}
                {totalApproved > 0 && (
                    <span className="ml-1.5 text-[10px] font-mono tabular-nums text-muted-foreground" aria-label={`${totalApproved} approved ideas`}>
                        · {totalApproved}
                    </span>
                )}
            </Button>
            <span
                data-testid={`partner-pdf-summary-${streamId}`}
                className="text-[10px] font-mono text-muted-foreground mt-1 text-right"
            >
                {summary}
            </span>
        </div>
    );
}
